import { Attr, BelongsTo, HasMany, Model } from "spraypaint";

import { ApplicationRecord } from "shared/services/spraypaint";
import { type Nullable } from "shared/types";
import {
  type BroadcastProvider,
  type MediaRequestStatus,
} from "shared/types/mentions";

import type AccountManager from "./AccountManager";
import type ProgramAiring from "./ProgramAiring";
import type SourceRecord from "./SourceRecord";
import type User from "./User";
import type ViewedItem from "./ViewedItem";

@Model()
class MediaRequest extends ApplicationRecord {
  static jsonapiType = "media_requests";

  @BelongsTo() accountManager: AccountManager;

  @Attr() accountManagerId: number;

  @Attr({ persist: false }) archivedAt: string;

  @Attr({ persist: false }) audience: number;

  @Attr() billable: boolean;

  @Attr({ persist: false }) broadcastProvider?: Nullable<BroadcastProvider>;

  @Attr() comment: string;

  @Attr() completedAt: string;

  @Attr({ persist: false }) createdAt: string;

  @Attr() endTime: number;

  @Attr({ persist: false }) humanType: string;

  @Attr() instantDownload: boolean;

  @Attr() lockVersion: number;

  @Attr({ persist: false }) mediaType: string;

  @Attr({ persist: false }) mediaUrl: string;

  @Attr({ persist: false }) medium: string;

  @Attr() notes: string;

  @Attr() notifyOnCompletion: boolean;

  @Attr() organisationTeamId: number;

  @Attr() originId: number;

  @Attr() originKeyword: string;

  @Attr({ persist: false }) originLabel: string;

  @Attr() originType: string;

  @Attr() playerUrl: string;

  @BelongsTo() programAiring: ProgramAiring;

  @Attr() recipients: string;

  @BelongsTo() source: SourceRecord;

  @Attr() sourceId: number;

  @Attr() sourceType: string;

  @Attr() startTime: number;

  @Attr() status: MediaRequestStatus;

  @Attr() summary: string;

  @HasMany() thumbnails: any;

  @Attr() thumbnailUrl: string;

  @Attr() title: string;

  @Attr() token: string;

  @Attr() type: string;

  @Attr({ persist: false }) updatedAt: string;

  @Attr() url: string;

  @BelongsTo() user: User;

  @Attr() userId: number;

  @HasMany({ name: "target" }) viewedItems: ViewedItem[];

  duplicate() {
    // Deletes properties we can't write with .save()
    const {
      createdAt,
      humanType,
      id,
      mediaType,
      originLabel,
      playerUrl,
      token,
      type,
      ...attrs
    } = this;

    return attrs;
  }

  duplicateCancelled() {
    // Deletes properties we can't write from a cancelled Media Request.
    const {
      archivedAt,
      comment,
      createdAt,
      humanType,
      id,
      mediaType,
      originLabel,
      playerUrl,
      status,
      token,
      type,
      ...attrs
    } = this;

    return attrs;
  }

  async refreshLockVersion() {
    this.lockVersion = (
      await MediaRequest.where({ id: this.id })
        .select(["lockVersion"])
        .find(this.id as string)
    ).data.lockVersion;
  }
}

export default MediaRequest;
